import { baseCSS, openPrint } from './workflowPDF';
import {
  identidadeDoLaboratorio, assinaturaDoRodape, linhaDoResponsavel,
} from './laboratorioNoRelatorio';

/**
 * Impressão dos relatórios.
 *
 * NÃO RECALCULA NADA. Recebe os números já prontos do painel — os mesmos
 * `useMemo` que desenharam a tela — e a MESMA especificação de colunas que a
 * `Tabela` usa. Um `valor: (l) => ...` escrito uma vez serve aos dois.
 *
 * É a única defesa que funciona contra o papel discordar da tela. O PDF da FIV
 * recalculava o descarte por subtração e chegava a outro total; ninguém
 * percebeu por semanas, porque só quem imprime vê o número impresso.
 *
 * OS FILTROS VÃO NO CABEÇALHO, SEMPRE.
 *   Relatório filtrado que não diz que está filtrado é pior que relatório
 *   nenhum: quem recebe a folha lê como o retrato inteiro do laboratório. O
 *   período aparece mesmo quando é o padrão, e cada filtro extra ganha sua
 *   linha.
 */

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const dataBR = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('pt-BR') : '—');

/**
 * As linhas de "o que está sendo mostrado".
 *
 * Só entra o que restringe. Listar "Cliente: todos" ocuparia espaço para dizer
 * que nada foi filtrado — o silêncio já diz isso.
 */
export const descreverFiltros = ({
  de, ate, cliente, sebraeProjeto, touro, agrupamento, periodo = true,
}) => {
  const linhas = [];
  // O período sai SEMPRE, mesmo sendo o padrão e mesmo sem data — "— a —" diz
  // "sem recorte", e dizer é melhor que calar.
  //
  // `periodo: false` é outra coisa, e por isso é explícito: o relatório de
  // sêmen não tem dimensão de tempo nenhuma. Ele é um saldo de hoje, e uma
  // linha de período ali seria a folha afirmando que mede um intervalo que ela
  // não mede. Sem o parâmetro, "sem data" continua virando travessão — as duas
  // situações se pareciam no código e não são a mesma.
  if (periodo) linhas.push(['Período', `${dataBR(de)} a ${dataBR(ate)}`]);
  if (cliente)       linhas.push(['Cliente', cliente]);
  if (sebraeProjeto) linhas.push(['Projeto SEBRAE', sebraeProjeto]);
  if (touro)         linhas.push(['Touro', touro]);
  if (agrupamento)   linhas.push(['Agrupado por', agrupamento]);
  return linhas;
};

const cabecalho = (titulo, laboratorio, filtros) => `
  <div class="page-header">
    ${identidadeDoLaboratorio({ nome: laboratorio })}
    <div class="header-right">
      <div class="client">${esc(titulo)}</div>
      <div class="meta">Emitido em ${new Date().toLocaleDateString('pt-BR')}</div>
    </div>
  </div>
  <div class="session-info">
    ${filtros.map(([r, v]) => `<span><b>${esc(r)}:</b> ${esc(v)}</span>`).join('')}
  </div>`;

/** Os indicadores do topo do painel, na mesma ordem em que aparecem na tela. */
const blocoIndicadores = (indicadores = []) => {
  if (indicadores.length === 0) return '';
  return `
    <table class="indicadores">
      <tbody><tr>
        ${indicadores.map(i => `
          <td>
            <div class="ind-rotulo">${esc(i.rotulo)}</div>
            <div class="ind-valor">${esc(i.valor)}</div>
            ${i.detalhe ? `<div class="ind-detalhe">${esc(i.detalhe)}</div>` : ''}
          </td>`).join('')}
      </tr></tbody>
    </table>`;
};

/**
 * Uma tabela a partir da especificação de colunas do painel.
 *
 * `c.valor(l)` pode devolver JSX na tela; aqui só serve o que vira texto. Por
 * isso as colunas dos painéis devolvem string — e quando uma delas precisar de
 * marcação, o lugar de tratar é aqui, não duplicando a coluna.
 */
const blocoTabela = (titulo, colunas, linhas, vazio = null) => {
  if (!colunas?.length) return '';
  // Tabela vazia some — a menos que o vazio seja a notícia. "Nenhuma parcela
  // vencida" num resumo para o dono vale tanto quanto a lista delas.
  if (!linhas?.length) {
    return vazio
      ? `<div class="section-title">${esc(titulo)}</div><div class="vazio">${esc(vazio)}</div>`
      : '';
  }
  return `
    <div class="section-title">${esc(titulo)}</div>
    <table>
      <thead><tr>
        ${colunas.map(c => `<th${c.numerica ? ' class="dir"' : ''}>${esc(c.rotulo)}</th>`).join('')}
      </tr></thead>
      <tbody>
        ${linhas.map(l => `<tr>
          ${colunas.map(c => {
            const bruto = c.valor(l);
            const txt = (bruto === null || bruto === undefined || bruto === '') ? '—' : bruto;
            const classes = [c.numerica ? 'dir' : '', c.forte ? 'num' : ''].filter(Boolean).join(' ');
            return `<td${classes ? ` class="${classes}"` : ''}>${esc(txt)}</td>`;
          }).join('')}
        </tr>`).join('')}
      </tbody>
    </table>`;
};

const CSS_EXTRA = `
  .indicadores { border: none; margin-bottom: 12px; }
  .indicadores td { border: 1px solid #e2e8f0; padding: 7px 10px; vertical-align: top; width: 1%; }
  .ind-rotulo { font-size: 9px; text-transform: uppercase; letter-spacing: .04em; color: #64748b; }
  .ind-valor { font-size: 15px; font-weight: 700; color: #111; margin-top: 2px; }
  .ind-detalhe { font-size: 9px; color: #94a3b8; margin-top: 1px; }
  .dir { text-align: right; }
  .section-title { margin-top: 14px; }
  .vazio { border: 1px solid #e2e8f0; border-top: none; padding: 6px 10px; font-size: 11px; color: #64748b; }
  .secao { margin-top: 22px; page-break-before: auto; }
  .secao > h2 { font-size: 16px; color: #111; border-bottom: 2px solid #334155; padding-bottom: 4px; margin-bottom: 10px; }
  .secao-nota { font-size: 10px; color: #64748b; margin: -6px 0 8px; }
  /* A tabela de detalhe passa da folha com frequência: o total tem que
     sobreviver à quebra, então ele vai ANTES, no bloco de indicadores. */`;

/**
 * Monta e abre o relatório.
 *
 * `grupos` é opcional e sai antes do detalhe: quem imprime costuma querer o
 * consolidado por cliente ou doadora, e a lista item a item é o anexo.
 */
/**
 * O corpo do relatório, separado da impressão.
 *
 * Separado para poder ser conferido sem navegador: é aqui que a coluna da tela
 * vira texto, e é o lugar onde um escape esquecido ou uma coluna que devolve
 * objeto passariam despercebidos até alguém imprimir.
 */
/**
 * Seções com título próprio, cada uma com indicadores e tabelas.
 *
 * É o formato do resumo de gestão — Resumo, Produção, Financeiro, Equipe —,
 * em que cada seção responde "feito" e "em aberto" com as mesmas duas peças
 * que os relatórios já usam.
 */
const blocoSecoes = (secoes = []) => secoes.map(s => `
  <div class="secao">
    <h2>${esc(s.titulo)}</h2>
    ${s.nota ? `<p class="secao-nota">${esc(s.nota)}</p>` : ''}
    ${blocoIndicadores(s.indicadores)}
    ${(s.tabelas || []).map(t => blocoTabela(t.titulo, t.colunas, t.linhas, t.vazio)).join('')}
  </div>`).join('');

export function montarRelatorioHTML({
  titulo, laboratorio, filtros = [], indicadores = [],
  grupos, detalhe, secoes,
}) {
  return `
    <style>${baseCSS()}${CSS_EXTRA}</style>
    ${cabecalho(titulo, laboratorio, filtros)}
    ${blocoIndicadores(indicadores)}
    ${grupos ? blocoTabela(grupos.titulo, grupos.colunas, grupos.linhas) : ''}
    ${detalhe ? blocoTabela(detalhe.titulo, detalhe.colunas, detalhe.linhas) : ''}
    ${secoes ? blocoSecoes(secoes) : ''}
    <div class="footer">
      ${esc(titulo)} · ${assinaturaDoRodape(laboratorio)}${secoes ? '' : ` ·
      ${(detalhe?.linhas?.length ?? 0)} linha(s)`}
      ${linhaDoResponsavel()}
    </div>`;
}

export function gerarRelatorioPDF(spec) {
  openPrint(spec.titulo, montarRelatorioHTML(spec));
}
